// ============================================================
// SaveConflictToast.tsx
// Global toast for optimistic-concurrency conflicts: when a case or
// client autosave gets a 409 because someone else saved the record
// first. A conflict is a NON-DESTRUCTIVE warning (the user's edit is
// kept on screen, nothing is silently overwritten), so this uses the
// app's amber warning styling — distinct from the red SaveIndicator
// error. Reads the store's saveConflict slot, which the autosave hooks
// set on a 409. Auto-dismisses after 8s (longer than the favourites
// toast — the message asks the user to re-enter their change, so give
// them time to read it) and is dismissible via the × .
//
// Mounted once globally (App.tsx) so it covers both the case detail and
// the client detail without per-surface wiring — mirrors
// FavouritesErrorToast.
// ============================================================
import React, { useEffect } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'

const SaveConflictToast: React.FC = () => {
  const saveConflict = useCasevoStore((s) => s.saveConflict)
  const setSaveConflict = useCasevoStore((s) => s.setSaveConflict)

  // Auto-dismiss. Keyed on the message so each new conflict restarts the
  // 8s timer; cleared on unmount / message change.
  useEffect(() => {
    if (!saveConflict) return
    const timer = window.setTimeout(() => setSaveConflict(null), 8000)
    return () => window.clearTimeout(timer)
  }, [saveConflict, setSaveConflict])

  if (!saveConflict) return null

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] max-w-[90vw]"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700/50 text-sm text-amber-900 dark:text-amber-100">
        <span className="min-w-0">{saveConflict}</span>
        <button
          type="button"
          onClick={() => setSaveConflict(null)}
          aria-label="Dismiss"
          className="shrink-0 text-amber-900 dark:text-amber-100 hover:opacity-70 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

export default SaveConflictToast
